import { useEffect, useSyncExternalStore } from "react";

/**
 * The magnifier in the mobile header should search whatever the current
 * page is about: posts on Home, people on the Tree, members on Members.
 *
 * Pages with a search box register it here while mounted. The header reads
 * the registry when tapped; if nothing is registered (Events, News, …) it
 * falls back to the Home post search.
 */
export type HeaderSearch = {
  /** What gets searched, for the button's tooltip: "posts", "members"… */
  label: string;
  /** Open/scroll to/focus the page's search input. */
  focus: () => void;
};

let current: HeaderSearch | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function getHeaderSearch(): HeaderSearch | null {
  return current;
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Header-side: re-renders when a page registers or unregisters. */
export function useCurrentHeaderSearch(): HeaderSearch | null {
  return useSyncExternalStore(subscribe, getHeaderSearch, () => null);
}

/** Page-side: make the header magnifier open this page's search box. */
export function useHeaderSearch(label: string, focus: () => void) {
  useEffect(() => {
    const entry: HeaderSearch = { label, focus };
    current = entry;
    emit();
    return () => {
      if (current === entry) {
        current = null;
        emit();
      }
    };
    // `focus` closes over refs only; re-registering on every render would
    // just churn the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);
}
